import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './roles.css';
import { ROUTES } from '../../../constants/routes';
import {
  useDeleteRoleMutation,
  useRolesQuery,
} from '../../../hooks/useDashboardQueries';
import { RoleV2 } from '../../../types/role';
import { RolesFilterState } from '../../../types/dashboardV2';
import { useListPage } from '../../../hooks/useListPage';
import { DashboardQueryKey } from '../../../constants/dashboardQueryKeys';
import { RolesTable } from './RolesTable';
import { RolesTableSkeleton } from './RolesTableSkeleton';
import { MobileRoleCard } from './MobileRoleCard';
import { MobileCardSkeleton } from '../../../components/common/MobileCardSkeleton/MobileCardSkeleton';
import { ListPageLayout } from '../components/ListPageLayout';
import { ListPageToolbar } from '../components/ListPageToolbar';
import { DeleteRoleDialog } from './DeleteRoleDialog';
import { ORGANIZATIONS_ROLES_SECTION } from '../../../layouts/DashboardV2Layout/dashboardSections';

const INITIAL_FILTERS: RolesFilterState = { search: '' };

export const RolesV2 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [roleToDelete, setRoleToDelete] = useState<RoleV2 | null>(null);

  const { filters, handleFilterChange } = useListPage<RolesFilterState>({
    initialFilters: INITIAL_FILTERS,
    queryKeyRoot: DashboardQueryKey.ROLES,
    urlSync: true,
  });
  const search = filters.search;

  const { data: roles, isLoading, isFetching, error, refetch } = useRolesQuery();
  const deleteMutation = useDeleteRoleMutation();

  const filtered = useMemo(() => {
    if (!roles) return [];
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) => r.name.toLowerCase().includes(q));
  }, [roles, search]);

  const handleCreate = () =>
    navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_ROLE_CREATE}`);

  const handleEdit = (role: RoleV2) =>
    navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_ROLES}/${role.id}`);

  const errorMessage =
    error instanceof Error ? error.message : t('dashboard.roles.errorLoading');

  return (
    <>
      <ListPageLayout
        section={ORGANIZATIONS_ROLES_SECTION}
        desktopToolbar={
          <ListPageToolbar
            searchValue={search}
            onSearchChange={(value) => handleFilterChange({ search: value })}
            searchPlaceholder={t('dashboard.roles.searchPlaceholder')}
            countText={t('dashboard.roles.displayingOfTotal', {
              count: filtered.length,
              total: roles?.length ?? 0,
            })}
            actionLabel={t('dashboard.roles.create')}
            onAction={handleCreate}
          />
        }
        showLoading={isLoading || isFetching}
        isLoading={isLoading}
        hasError={Boolean(error)}
        errorMessage={errorMessage}
        onRetry={() => refetch()}
        itemCount={filtered.length}
        emptyState={{ icon: 'admin_panel_settings', message: t('dashboard.roles.empty') }}
        table={
          <RolesTable
            roles={filtered}
            onEdit={handleEdit}
            onDelete={setRoleToDelete}
          />
        }
        tableSkeleton={<RolesTableSkeleton />}
        mobileTitle={t('dashboard.sidebar.organizationsRoles')}
        mobileHeaderAction={
          <button
            type="button"
            className="mobile-icon-btn"
            onClick={handleCreate}
            aria-label={t('dashboard.roles.create')}
          >
            <span className="material-symbols-outlined">add</span>
          </button>
        }
        mobileCountText={t('dashboard.roles.allWithCount', { count: filtered.length })}
        search={{
          value: search,
          onChange: (value) => handleFilterChange({ search: value }),
          placeholder: t('dashboard.roles.searchPlaceholder'),
          inputProps: { type: 'text', name: 'mobile-role-search' },
        }}
        mobileCards={filtered.map((role) => (
          <MobileRoleCard
            key={role.id}
            role={role}
            onEdit={() => handleEdit(role)}
            onDelete={() => setRoleToDelete(role)}
          />
        ))}
        mobileSkeleton={<MobileCardSkeleton showFooter={false} idValueWidth="8rem" />}
      />

      <DeleteRoleDialog
        role={roleToDelete}
        availableRoles={(roles ?? []).filter((r) => r.id !== roleToDelete?.id)}
        onClose={() => setRoleToDelete(null)}
        onConfirm={async (options) => {
          if (!roleToDelete) return;
          await deleteMutation.mutateAsync({
            roleId: roleToDelete.id,
            options,
          });
          setRoleToDelete(null);
        }}
        isSubmitting={deleteMutation.isPending}
      />
    </>
  );
};

export default RolesV2;
